import React, {
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Dayjs } from "dayjs";
import { MisaPUOrder, PODetailEnriched, OdooPOPayload, OdooPOOrderLine } from "../types/misa";
import {
  fetchPUOrders,
  fetchPUOrderDetails,
  fetchVendors,
  fetchOdooVendors,
  fetchOdooProduct,
  createOdooPurchaseOrder,
} from "../services/api";

// ─── Column definitions ───────────────────────────────────────────────────────
const puOrderColumns = [
  { id: "__checkbox",    label: "",              minWidth: 48,  align: "center" as const },
  { id: "RefDate",       label: "Ngày",          minWidth: 90,  align: "center" as const },
  { id: "RefNo",         label: "Số PO",         minWidth: 120, align: "left"   as const },
  { id: "VendorName",    label: "Nhà cung cấp",  minWidth: 200, align: "left"   as const },
  { id: "VendorCode",    label: "Mã NCC",         minWidth: 120, align: "left"   as const },
  { id: "OdooPartnerId",label: "Odoo ID",         minWidth: 90,  align: "center" as const },
  { id: "TotalAmount",   label: "Tổng tiền",    minWidth: 100, align: "right"  as const },
  { id: "Status",        label: "Trạng thái",    minWidth: 130, align: "center" as const },
  { id: "JournalMemo",   label: "Diễn giải",    minWidth: 300, align: "left"   as const },
];

const STATUS_CONFIG: Record<number, { label: string; bg: string; color: string }> = {
  0: { label: "Chưa thực hiện", bg: "#f1f5f9", color: "#64748b" },
  1: { label: "Đang thực hiện", bg: "#fff7ed", color: "#ea580c" },
  2: { label: "Đã thực hiện",   bg: "#f0fdf4", color: "#16a34a" },
};

// ─── Exposed ref handle ───────────────────────────────────────────────────────
export interface PUOrderTableHandle {
  /** Gọi từ App.tsx để fetch và hiện bảng PO */
  fetchData: () => Promise<void>;
}

// ─── Props ────────────────────────────────────────────────────────────────────
export interface PUOrderTableProps {
  misaToken: string;
  selectedBranch: string;
  fromDate: Dayjs;
  toDate: Dayjs;
  /** Thông báo App bắt đầu / kết thúc loading */
  onLoadingChange: (loading: boolean) => void;
  /** Thông báo App chuyển sang view "po" */
  onDataLoaded: () => void;
  /** Pagination props dùng chung với App */
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  onPageChange: (_: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
const PUOrderTable = forwardRef<PUOrderTableHandle, PUOrderTableProps>(
  (
    {
      misaToken,
      selectedBranch,
      fromDate,
      toDate,
      onLoadingChange,
      onDataLoaded,
      page,
      rowsPerPage,
      isLoading,
      onPageChange,
      onRowsPerPageChange,
    },
    ref,
  ) => {
    const [puOrders, setPuOrders] = useState<MisaPUOrder[]>([]);
    const [selectedPUOrderIds, setSelectedPUOrderIds] = useState<Set<string>>(new Set());
    const [searchRefNo, setSearchRefNo] = useState("");
    const [searchJournalMemo, setSearchJournalMemo] = useState("");

    // ── Fetch PUOrders + enrich VendorCode + OdooPartnerId ───────────────────
    const fetchData = async () => {
      if (!misaToken) {
        console.warn("⚠️ Chưa có MISA token");
        return;
      }
      const formattedFromDate = fromDate.format("YYYY-MM-DD");
      const formattedToDate = toDate.format("YYYY-MM-DD");
      onLoadingChange(true);
      try {
        // Fetch 3 nguồn song song
        const [data, vendors, odooVendors] = await Promise.all([
          fetchPUOrders(misaToken, formattedFromDate, formattedToDate, selectedBranch),
          fetchVendors(misaToken, selectedBranch),
          fetchOdooVendors(),
        ]);

        // Map VendorID -> VendorCode (MISA)
        const vendorMap = new Map<string, string>();
        vendors.forEach((v) => {
          if (v.VendorID && v.VendorCode) vendorMap.set(v.VendorID, v.VendorCode);
        });
        console.log("🏢 MISA Vendors map:", Object.fromEntries(vendorMap));

        // Map VendorCode -> Odoo partner id
        // Odoo dùng field "code" để lưu mã NCC (ví dụ "CT0000000193")
        const odooMap = new Map<string, number>();
        odooVendors.forEach((p) => {
          const key = (p.code ?? p.ref ?? "").trim();
          if (key && p.id) odooMap.set(key, p.id);
        });
        console.log("📦 Odoo partner map (code → id):", Object.fromEntries(odooMap));

        // Enrich + filter bỏ Status === 2
        const enriched = data
          .filter((order) => order.Status !== 2)
          .map((order) => {
            const vendorCode = order.VendorID ? (vendorMap.get(order.VendorID) ?? "") : "";
            const odooPartnerId = vendorCode ? (odooMap.get(vendorCode.trim()) ?? undefined) : undefined;
            return {
              ...order,
              VendorCode: vendorCode,
              OdooPartnerId: odooPartnerId,
            };
          });

        setPuOrders(enriched);
        setSelectedPUOrderIds(new Set());
        onDataLoaded();
        console.log(`📦 PU Orders enriched: ${enriched.length} / ${data.length} phiếu`, enriched);
      } catch (error) {
        console.error("Lỗi khi lấy dữ liệu PO:", error);
      } finally {
        onLoadingChange(false);
      }
    };

    // Expose fetchData ra ngoài qua ref
    useImperativeHandle(ref, () => ({ fetchData }));

    // ── Xem chi tiết các PO đã chọn ───────────────────────────────────────────
    const handleViewPUOrderDetails = async () => {
      if (selectedPUOrderIds.size === 0) {
        console.warn("⚠️ Chưa chọn PO nào");
        return;
      }
      console.log(`🔍 Xem chi tiết ${selectedPUOrderIds.size} PO...`);

      for (const refId of selectedPUOrderIds) {
        const order = puOrders.find((o) => o.RefID === refId);
        console.log(
          `\n📄 PO: ${order?.RefNo ?? refId} — ${order?.VendorName ?? ""} (${order?.VendorCode ?? ""}) | Odoo Partner ID: ${order?.OdooPartnerId ?? "chưa có"}`,
        );

        // 1. Lấy chi tiết MISA
        const details = await fetchPUOrderDetails(misaToken, refId, selectedBranch);
        console.log(`  📊 Chi tiết MISA (${details.length} dòng):`, details);

        // 2. Lấy danh sách SKU unique
        const skuCodes = [...new Set(
          details
            .map((d: any) => (d.SKUCode ?? d.InventoryItemCode ?? "").trim())
            .filter(Boolean),
        )] as string[];

        // 3. Tìm tất cả sản phẩm Odoo tương ứng song song
        const odooProductMap = new Map<string, any>();
        if (skuCodes.length > 0) {
          const fetched = await Promise.all(
            skuCodes.map(async (sku) => {
              const products = await fetchOdooProduct(sku);
              return { sku, product: products[0] ?? null }; // lấy kết quả đầu tiên
            }),
          );
          fetched.forEach(({ sku, product }) => odooProductMap.set(sku, product));
        }

        // 4. Build mảng enriched
        const enriched: PODetailEnriched[] = details.map((d: any) => {
          const sku = (d.SKUCode ?? d.InventoryItemCode ?? "").trim();
          const odoo = odooProductMap.get(sku) ?? null;
          return {
            // Header PO
            refId,
            refNo:             order?.RefNo,
            vendorId:          order?.VendorID,
            vendorCode:        order?.VendorCode,
            vendorName:        order?.VendorName,
            odooPartnerId:     order?.OdooPartnerId,

            // Dòng hàng MISA
            skuCode:           sku || undefined,
            inventoryItemName: d.InventoryItemName,
            quantity:          d.Quantity,
            quantityReceipt:   d.QuantityReceipt ?? 0,   // đã nhập kho
            unitName:          d.UnitName,
            unitPrice:         d.UnitPrice,
            amount:            d.Amount,
            discountRate:      d.DiscountRate,
            discountAmount:    d.DiscountAmount,
            vatRate:           d.VATRate,
            vatAmount:         d.VATAmount,
            payAmount:         d.PayAmount,
            amountAfterDiscount: d.AmountAfterDiscount,
            lotNo:             d.LotNo,
            expiryDate:        d.ExpiryDate,

            // Sản phẩm Odoo
            odooProductId:     odoo?.id,
            odooProductName:   odoo?.name,
            odooListPrice:     odoo?.list_price,
            odooStandardPrice: odoo?.standard_price,
            odooUomName:       odoo?.uom_name,
            odooCategName:     odoo?.categ_name,
            odooBarcode:       odoo?.barcode,
            odooMatchFound:    odoo !== null,
          };
        });

        console.log(`  🚀 Enriched data (${enriched.length} dòng):`, enriched);

        // 5. Build Odoo PO payload
        if (!order?.OdooPartnerId) {
          console.warn("  ⚠️ Không có OdooPartnerId — bỏ qua build payload");
        } else {
          // Map: VAT rate (%) → Odoo tax id  (cập nhật theo cấu hình Odoo thực tế)
          const VAT_TAX_MAP: Record<number, number> = {
            0: 4,  // không có thuế
            5: 3,  // thuế 5%  → id=3
            8: 2,  // thuế 8%  → id=2  (điều chỉnh theo Odoo)
            10: 1, // thuế 10% → id=1
          };
          const r2 = (n: number) => Math.round(n * 100) / 100;

          const order_line: OdooPOOrderLine[] = enriched
            .filter((r) => {
              if (!r.odooMatchFound || !r.odooProductId) return false;
              const remaining = (r.quantity ?? 0) - (r.quantityReceipt ?? 0);
              if (remaining <= 0) {
                console.log(`    ⏭️ [${r.skuCode}] bỏ qua — SL còn lại = ${remaining}`);
                return false;
              }
              return true;
            })
            .map((r) => {
              const qty   = (r.quantity ?? 0) - (r.quantityReceipt ?? 0);  // số lượng còn cần nhập
              const price = r.unitPrice ?? 0;
              const ckPct   = r.discountRate ?? 0;
              const vatPct  = Math.round(r.vatRate ?? 0);

              const originalQty = r.quantity && r.quantity > 0 ? r.quantity : 1;
              const ratio = qty / originalQty;

              const totalBeforeDiscount  = r.amount ? r2(r.amount * ratio) : r2(qty * price);
              const priceDiscounted      = r2((r.discountAmount ?? 0) * ratio);
              const subtotal             = r2(totalBeforeDiscount - priceDiscounted);
              const priceTax             = r2((r.vatAmount ?? 0) * ratio);
              const priceTotal           = r.payAmount ? r2(r.payAmount * ratio) : r2(subtotal + priceTax);

              const taxId = VAT_TAX_MAP[vatPct];
              const odoo = odooProductMap.get(r.skuCode ?? "");

              // Map tên đơn vị MISA → packaging_id Odoo
              // packaging_ids: [{ id, name: "Hộp" }, { id, name: "Vỉ" }, ...]
              const packagings: any[] = odoo?.packaging_ids ?? [];
              const unitNameMisa = (r.unitName ?? "").trim().toLowerCase();
              const matchedPkg = packagings.find(
                (p: any) => (p.name ?? "").trim().toLowerCase() === unitNameMisa,
              );
              // Fallback: nếu không khớp tên thì lấy cái đầu tiên
              const packagingId = matchedPkg?.id ?? packagings[0]?.id ?? null;

              if (packagings.length > 0) {
                console.log(
                  `    📦 [${r.skuCode}] đvt="${r.unitName}" → packaging_ids:`,
                  packagings.map((p: any) => `${p.id}:${p.name}`).join(", "),
                  `→ chọn id=${packagingId}${matchedPkg ? " (khớp tên)" : " (fallback đầu tiên)"}`,
                );
              }

              return {
                product_id:              r.odooProductId!,
                product_packaging_id:    packagingId,
                product_packaging_qty:   qty,
                packaging_price_unit:    price,
                manual_discount:         ckPct,
                manual_price_discounted: priceDiscounted,
                manual_price_subtotal:   subtotal,
                manual_price_tax:        priceTax,
                manual_price_total:      priceTotal,
                taxes_id:                taxId ? [{ id: taxId }] : [],
                note:                    "",
              };
            });

          const now = new Date();
          const pad = (n: number) => String(n).padStart(2, "0");
          const dateOrder = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

          const payload: OdooPOPayload = {
            partner_id:     order.OdooPartnerId,
            description:    `Đặt hàng từ MISA PO ${order.RefNo ?? refId}`,
            date_order:     dateOrder,
            receipt_status: "pending",
            order_line,
          };

          console.log("📦 Odoo PO Payload:", JSON.stringify(payload, null, 2));
          if (enriched.some((r) => !r.odooMatchFound)) {
            const missing = enriched.filter((r) => !r.odooMatchFound).map((r) => r.skuCode);
            console.warn(`  ⚠️ ${missing.length} SKU chưa có Odoo product, bỏ qua khỏi order_line:`, missing);
          }

          try {
            console.log(`⏳ Đang gửi PO lên Odoo...`);
            const response = await createOdooPurchaseOrder(payload);
            console.log("✅ Gửi Odoo thành công:", response);
            alert(`Đã gửi thành công PO ${order.RefNo ?? refId} lên Odoo!`);
          } catch (error) {
            console.error("❌ Lỗi khi gửi PO lên Odoo:", error);
            alert(`Lỗi khi tạo PO ${order.RefNo ?? refId} trên Odoo!`);
          }
        }
      }

      console.log("✅ Hoàn thành xem chi tiết PO");
    };

    // ── Filtered display data ─────────────────────────────────────────────────
    let displayPuOrders: MisaPUOrder[] = puOrders;
    if (searchRefNo.trim()) {
      displayPuOrders = displayPuOrders.filter((row) =>
        row.RefNo?.toLowerCase().includes(searchRefNo.trim().toLowerCase()),
      );
    }
    if (searchJournalMemo.trim()) {
      displayPuOrders = displayPuOrders.filter((row) =>
        (row.JournalMemo || "")
          .toLowerCase()
          .includes(searchJournalMemo.trim().toLowerCase()),
      );
    }

    // ── Checkbox helpers ──────────────────────────────────────────────────────
    const currentPageIds = displayPuOrders
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((r) => r.RefID ?? "")
      .filter(Boolean);

    const allChecked =
      currentPageIds.length > 0 &&
      currentPageIds.every((id) => selectedPUOrderIds.has(id));
    const someChecked = currentPageIds.some((id) => selectedPUOrderIds.has(id));

    const toggleAll = (checked: boolean) => {
      setSelectedPUOrderIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
        return next;
      });
    };

    const toggleRow = (refId: string, checked: boolean) => {
      setSelectedPUOrderIds((prev) => {
        const next = new Set(prev);
        checked ? next.add(refId) : next.delete(refId);
        return next;
      });
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <>
        {/* Toolbar: hiện khi có PO được chọn */}
        {selectedPUOrderIds.size > 0 && (
          <Box
            sx={{
              px: 2, py: 1,
              display: "flex", alignItems: "center", gap: 2,
              background: "#eff6ff", borderBottom: "1px solid #bfdbfe",
            }}
          >
            <Typography variant="body2" sx={{ color: "#1d4ed8", fontWeight: 600 }}>
              Đã chọn {selectedPUOrderIds.size} PO
            </Typography>
            <Button
              variant="contained" size="small"
              onClick={handleViewPUOrderDetails}
              sx={{ background: "#1d4ed8", "&:hover": { background: "#1e40af" } }}
            >
              🔍 Tạo PO trên Odoo
            </Button>
            <Button
              variant="outlined" size="small"
              onClick={() => setSelectedPUOrderIds(new Set())}
              sx={{ color: "#64748b", borderColor: "#cbd5e1" }}
            >
              Bỏ chọn tất cả
            </Button>
          </Box>
        )}

        {/* Table */}
        <TableContainer sx={{ maxHeight: "65vh", overflowY: "scroll", overflowX: "auto" }}>
          <Table stickyHeader aria-label="po table" style={{ width: "100%", minWidth: 500, tableLayout: "fixed" }}>
            <TableHead>
              {/* Header row */}
              <TableRow style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 11 }}>
                {puOrderColumns.map((col) => {
                  if (col.id === "__checkbox") {
                    return (
                      <TableCell key={col.id} align={col.align} style={{ fontWeight: 600, background: "#f8fafc", zIndex: 11, width: col.minWidth }}>
                        <Checkbox
                          size="small"
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={col.id} align={col.align} style={{ fontWeight: 600, background: "#f8fafc", zIndex: 11, width: col.minWidth }}>
                      {col.label}
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Search row */}
              <TableRow style={{ position: "sticky", top: 40, background: "#f8fafc", zIndex: 10 }}>
                {puOrderColumns.map((col) => {
                  if (col.id === "__checkbox")
                    return <TableCell key={col.id} style={{ background: "#f8fafc", width: col.minWidth }} />;
                  if (col.id === "RefNo")
                    return (
                      <TableCell key={col.id} align={col.align} style={{ background: "#f8fafc", width: col.minWidth }}>
                        <input
                          type="text" placeholder="Tìm số PO" value={searchRefNo}
                          onChange={(e) => { setSearchRefNo(e.target.value); onPageChange(null, 0); }}
                          style={{ width: "90%", fontSize: 15, padding: "6px 10px", border: "1px solid #bdbdbd", borderRadius: 4, background: "#fff", outline: "none" }}
                        />
                      </TableCell>
                    );
                  if (col.id === "JournalMemo")
                    return (
                      <TableCell key={col.id} align={col.align} style={{ background: "#f8fafc", width: col.minWidth }}>
                        <input
                          type="text" placeholder="Tìm diễn giải" value={searchJournalMemo}
                          onChange={(e) => { setSearchJournalMemo(e.target.value); onPageChange(null, 0); }}
                          style={{ width: "90%", fontSize: 15, padding: "6px 10px", border: "1px solid #bdbdbd", borderRadius: 4, background: "#fff", outline: "none" }}
                        />
                      </TableCell>
                    );
                  return <TableCell key={col.id} style={{ background: "#f8fafc", width: col.minWidth }} />;
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={puOrderColumns.length} align="center" style={{ padding: "40px 20px", color: "#1976d2" }}>
                    Đang tải dữ liệu...
                  </TableCell>
                </TableRow>
              ) : displayPuOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={puOrderColumns.length} align="center" style={{ padding: "40px 20px", color: "#999" }}>
                    Không có dữ liệu PO. Vui lòng chọn ngày và nhấn "Lấy dữ liệu PO"
                  </TableCell>
                </TableRow>
              ) : (
                displayPuOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const refId = row.RefID ?? "";
                    const isChecked = selectedPUOrderIds.has(refId);
                    return (
                      <TableRow
                        hover tabIndex={-1} key={`po-${index}`}
                        selected={isChecked}
                        sx={{ cursor: "pointer", background: isChecked ? "#eff6ff" : undefined }}
                      >
                        {puOrderColumns.map((col) => {
                          if (col.id === "__checkbox")
                            return (
                              <TableCell key={col.id} align={col.align}>
                                <Checkbox
                                  size="small" checked={isChecked}
                                  onChange={(e) => toggleRow(refId, e.target.checked)}
                                />
                              </TableCell>
                            );

                          const val = row[col.id];

                          if (col.id === "RefDate")
                            return (
                              <TableCell key={col.id} align={col.align}>
                                {val ? new Date(val as string).toLocaleDateString("vi-VN") : ""}
                              </TableCell>
                            );

                          if (col.id === "TotalAmount")
                            return (
                              <TableCell key={col.id} align={col.align}>
                                {val ? Number(val).toLocaleString("vi-VN") : ""}
                              </TableCell>
                            );

                          if (col.id === "Status") {
                            const cfg = STATUS_CONFIG[val as number] ?? {
                              label: `${val ?? ""}`,
                              bg: "#f1f5f9",
                              color: "#64748b",
                            };
                            return (
                              <TableCell key={col.id} align={col.align}>
                                <span style={{
                                  display: "inline-block", padding: "3px 10px",
                                  borderRadius: 12, fontSize: 13, fontWeight: 600,
                                  background: cfg.bg, color: cfg.color,
                                  border: `1px solid ${cfg.color}33`, whiteSpace: "nowrap",
                                }}>
                                  {cfg.label}
                                </span>
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={col.id} align={col.align}>
                              {(val as string) || ""}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 50, 100]}
          component="div"
          count={displayPuOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage="Số dòng / trang:"
        />
      </>
    );
  },
);

PUOrderTable.displayName = "PUOrderTable";

export default PUOrderTable;
